import React, { useState, useRef, useEffect } from "react";
import "./SearchableDropDown.scss";
import AuiIcon from "../AuiDropdown/AuiIcon";

export type viewType = "vertical" | "horizontal";

export interface Props {
  mainClassName?: string;
  dropContentClass?: string;
  dropClassName?: string;
  mainId?: string;
  dropId?: string;
  dropdownLabel?: string;
  data?: string[];
  listInBtn?: boolean;
  placeHolder?: string;
  view?: viewType;
  required?: boolean;
  errorText?: string;
  onChange?: (selectedValue: string) => void;
  value?: string;
  setSelectedIndex?: (index: number) => void;
  clearError?: boolean;
  onSearch?: (searchTerm: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

const SearchableSingleSelectDropdown = ({
  mainClassName = "",
  mainId,
  dropdownLabel,
  dropClassName = "",
  dropId,
  dropContentClass = "",
  data = [],
  listInBtn = false,
  view = "vertical",
  placeHolder = "Select Value...",
  required = false,
  errorText = "",
  onChange,
  value: initialValue,
  setSelectedIndex,
  clearError = false,
  onSearch,
  isLoading,
  disabled,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string | undefined>(initialValue);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  useEffect(() => setValue(initialValue), [initialValue]);
  useEffect(() => { if (clearError) setError(""); }, [clearError]);
  useEffect(() => { if (disabled) setError(""); }, [disabled]);

  useEffect(() => {
    if (open) document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const validateField = (val?: string) => {
    if ((touched || !val) && (!val?.trim()) && !disabled) {
      setError(errorText);
    } else {
      setError("");
    }
  };

  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      setSearchTerm("");
      onSearch?.("");
      buttonRef.current?.focus();
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      setOpen(false);
      if (searchTerm !== "") setSearchTerm("");
      onSearch?.("");
      setError("");
    }
  };

  const handleToggle = () => {
    setOpen((prev) => {
      const willOpen = !prev;
      if (willOpen) {
        setActiveIndex(-1);
      } else {
        setSearchTerm("");
        onSearch?.("");
      }
      return willOpen;
    });
    setError("");
    if (!open) buttonRef.current?.focus();
  };

  const handleBlur = () => {
    setTouched(true);
    validateField(value);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    setOpen(true);
    setTouched(true);
    onSearch?.(term);
    setActiveIndex(-1);
  };

  const handleClear = () => {
    setSearchTerm("");
    if (open) {
      onSearch?.("");
    } else {
      setValue(undefined);
      setTouched(true);
      setError(errorText);
      onChange?.("");
    }
  };

  const clickedItem = (selectedValue: string, index?: number) => {
    setValue(selectedValue);
    setSearchTerm("");
    onSearch?.(""); 
    setOpen(false);
    setTouched(true);
    validateField(selectedValue);
    onChange?.(selectedValue);
    if (index !== undefined) setSelectedIndex?.(index);
    buttonRef.current?.focus();
  };

  const escapeRegExp = (text: string) => {
    return text.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
  };

  const getHighlightedText = (text: string, highlight: string) => {
    if (!highlight) return text;
    const regex = new RegExp(`(${escapeRegExp(highlight)})`, "gi");
    let uniqueKey = 0;
    const parts = text.split(regex);
    return parts.map((part) => {
      const isMatch = part.toLowerCase() === highlight.toLowerCase();
      const content = part.split("").map((char) =>
          char === " " ? (
            <span key={`space-${uniqueKey++}`}>&nbsp;</span>
          ) : (
            <span key={`char-${uniqueKey++}`}>{char}</span>
          )
        );

      return (
        <span key={`part-${uniqueKey++}`} className={isMatch ? "highlight" : undefined}>
          {content}
        </span>
      );
  });
  };

  const filteredData = data.filter((item) =>
    item.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === " ") {
      e.preventDefault();
      setSearchTerm((prev) => prev + " ");
    } else if (e.key === "Escape") {
      setOpen(false);
      buttonRef.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1 >= filteredData.length ? 0 : prev + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? filteredData.length - 1 : prev - 1));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < filteredData.length) {
        e.preventDefault();
        clickedItem(filteredData[activeIndex], activeIndex);
        setError("");
      }
    } else if (e.key === "Tab") {
      setOpen(false);
      setSearchTerm("");
      onSearch?.("");
    }
  };

  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  useEffect(() => {
    if (activeIndex >= 0) {
      const el = document.getElementById(`option-${activeIndex}`);
      el?.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
      });
    }
  }, [activeIndex]);

  const handleListItemKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, item: string, index: number) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      clickedItem(item, index);
    }
  };

  const renderValueOrSearch = () => {
    if (open) {
      return (
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          placeholder="Search..."
          className="search-input"
          onClick={(e) => e.stopPropagation()}
          disabled={disabled}
          aria-invalid={touched && !!error}
          aria-describedby={touched && error ? `${mainId}-error` : undefined}
          aria-activedescendant={activeIndex !== -1 ? `option-${activeIndex}` : undefined}
          aria-controls="dropdown-list"
          aria-haspopup="listbox"
          aria-expanded={open}
          role="combobox"
          aria-autocomplete="list"
          aria-labelledby={`${dropId}-label ${dropId}-value`}
          aria-required={required}
        />
      );
    }
    return (
      <div
        className="placeholdText"
        id={`${dropId}-value`}
        title={value ?? placeHolder}
      >
        {value ?? placeHolder}
        {isLoading && !value && (
          <span className="loader-spinner" data-testid="dropdown-loader" />
        )}
      </div>
    );
  };

  const renderOptions = () => {
    if (filteredData.length === 0) {
      return <div className="no-results">No data found</div>;
    }

    return (
        <ul id="dropdown-list" className="dropdown-list">
          {filteredData.map((item, index) => {
            const itemId = `option-${index}`;
            const isActive = activeIndex === index;
            const isSelected = value === item;

            return (
                <li 
                  key={itemId} 
                  className="dropdownList-li" 
                  role="none"
                >
                <button
                  ref={(el) => (itemRefs.current[index] = el)}
                  id={itemId}
                  aria-selected={isActive}
                  className={`dropdownList ${isActive ? "highlighted" : ""} ${
                    isSelected ? "selected-option" : ""
                  }`}
                  onClick={() => clickedItem(item, index)}
                  onKeyDown={(e) => handleListItemKeyDown(e, item, index)}
                  tabIndex={0}
                  type="button"
                  role="option"
                  aria-label={`${item} ${index + 1} of ${filteredData.length}`}
                  aria-posinset={index + 1}
                  aria-setsize={filteredData.length}
                >
                  {getHighlightedText(item, searchTerm)}
                </button>
              </li>
            );
          })}
        </ul>
      );
  };

  let suggestionText = "";
  if (filteredData.length === 0) {
    suggestionText = "No data found";
  } else {
    const addSuffix = filteredData.length > 1 ? "s" : "";
    suggestionText = `Updated: ${filteredData.length} suggestion${addSuffix} for '${searchTerm}'`;
  }

  return (
    <div
      className={`dropdown-wrapper ${mainClassName} ${view}`}
      id={mainId}
      ref={ref}
    >
       {dropdownLabel && (
        <label id={`${dropId}-label`}>
          {dropdownLabel}
          {required && (
            <>
              <span className="visually-hidden">&nbsp;required</span>
              <span className="requiredLabel" aria-hidden="true">
                *
              </span>
            </>
          )}
        </label>
      )}
      <div
        className={`dropdownText${required ? " required" : ""} ${
          disabled ? "dropdown-disabled" : ""
        }`}
      >
        <button
          className={`dropDown ${dropClassName}`}
          onClick={!disabled ? handleToggle : undefined}
          aria-describedby={touched && error ? `${dropId}-error` : undefined}
          disabled={disabled}
          aria-expanded={open}
          ref={buttonRef}
          onBlur={handleBlur}
          id={dropId}
          role="combobox"
          aria-controls="dropdown-list"
          aria-haspopup="listbox"
          aria-labelledby={`${dropId}-label ${dropId}-value`}
          aria-required={required}
        >
          {renderValueOrSearch()}
        </button>

        {(value || (searchTerm && open)) && !disabled && (
          <button
            className="close-icon"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            data-testid="clear-search-id"
            aria-label={`Clear ${dropdownLabel} option`}
          >
            <AuiIcon iconClass="closeIcon" svgheight="12" svgwidth="12" />
          </button>
        )}

        <button
          onClick={!disabled ? handleToggle : undefined}
          className="dropicon"
          tabIndex={-1}
          disabled={disabled}
        >
          <AuiIcon
            iconClass={open ? "arrowup" : "arrowdown"}
            svgheight="20"
            svgwidth="20"
          />
        </button>

        {touched && error && (
          <span className="dropError" id={`${dropId}-error`} role="alert">
            {error}
          </span>
        )}

        {open && (
          <div className={`dropContent ${dropContentClass}`}>
             {renderOptions()}
            <output
              id={`${dropId}-output`}
              className="visually-hidden"
              aria-live="polite"
              aria-atomic="true"
              aria-relevant="all"
            >
              {suggestionText}
            </output>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableSingleSelectDropdown;
